import { useCallback, useRef, useState } from 'react';
import { message } from 'antd';

export interface UseAudioRecorderReturn {
	isRecording: boolean;
	isPaused: boolean;
	hasRecording: boolean;
	durationMs: number;
	stream: MediaStream | null;
	isPlaying: boolean;
	playbackProgress: number;
	startRecording: () => Promise<void>;
	togglePause: () => void;
	deleteRecording: () => void;
	finalizeRecording: () => Promise<Blob | null>;
	playPreview: () => void;
	stopPreview: () => void;
}

const MAX_RECORDING_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export const useAudioRecorder = (): UseAudioRecorderReturn => {
	const [isRecording, setIsRecording] = useState(false);
	const [isPaused, setIsPaused] = useState(false);
	const [hasRecording, setHasRecording] = useState(false);
	const [durationMs, setDurationMs] = useState(0);
	const [stream, setStream] = useState<MediaStream | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [playbackProgress, setPlaybackProgress] = useState(0);

	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const recordingChunksRef = useRef<Blob[]>([]);
	const recordingTimerRef = useRef<number | null>(null);
	const recordedBlobRef = useRef<Blob | null>(null);
	const isResettingRef = useRef(false);
	const mediaStreamRef = useRef<MediaStream | null>(null);
	const finalizeResolverRef = useRef<((blob: Blob | null) => void) | null>(null);
	const durationMsRef = useRef(0);
	const previewAudioRef = useRef<HTMLAudioElement | null>(null);
	const previewTimerRef = useRef<number | null>(null);
	const previewUrlRef = useRef<string | null>(null);

	const clearRecordingTimer = useCallback(() => {
		if (recordingTimerRef.current) {
			window.clearInterval(recordingTimerRef.current);
			recordingTimerRef.current = null;
		}
	}, []);

	const stopMediaStream = useCallback(() => {
		if (mediaStreamRef.current) {
			mediaStreamRef.current.getTracks().forEach((track) => {
				track.stop();
			});
			mediaStreamRef.current = null;
			setStream(null);
		}
	}, []);

	const stopPreview = useCallback(() => {
		if (previewAudioRef.current) {
			previewAudioRef.current.pause();
			previewAudioRef.current = null;
		}
		if (previewTimerRef.current) {
			window.clearInterval(previewTimerRef.current);
			previewTimerRef.current = null;
		}
		if (previewUrlRef.current) {
			URL.revokeObjectURL(previewUrlRef.current);
			previewUrlRef.current = null;
		}
		setIsPlaying(false);
		setPlaybackProgress(0);
	}, []);

	const resetRecordingState = useCallback(() => {
		stopPreview();
		isResettingRef.current = true;
		clearRecordingTimer();
		if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
			try {
				mediaRecorderRef.current.stop();
			} catch (e) {
				// ignore
			}
		}
		stopMediaStream();
		mediaRecorderRef.current = null;
		recordingChunksRef.current = [];
		recordedBlobRef.current = null;
		finalizeResolverRef.current = null;
		setIsRecording(false);
		setIsPaused(false);
		setHasRecording(false);
		setDurationMs(0);
		durationMsRef.current = 0;
		setTimeout(() => {
			isResettingRef.current = false;
		}, 0);
	}, [stopPreview, clearRecordingTimer, stopMediaStream]);

	const startRecording = useCallback(async () => {
		if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
			message.error('Audio recording is not supported in this browser.');
			return;
		}

		try {
			const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
			mediaStreamRef.current = audioStream;
			setStream(audioStream);

			const mediaRecorder = new MediaRecorder(audioStream);
			recordingChunksRef.current = [];

			mediaRecorder.ondataavailable = (event) => {
				if (event.data && event.data.size > 0) {
					recordingChunksRef.current.push(event.data);
				}
			};

			mediaRecorder.onstop = () => {
				if (isResettingRef.current) {
					return;
				}
				clearRecordingTimer();
				stopMediaStream();
				if (recordingChunksRef.current.length > 0) {
					const blob = new Blob(recordingChunksRef.current, { type: 'audio/webm' });
					recordedBlobRef.current = blob;
					setHasRecording(true);
					if (finalizeResolverRef.current) {
						finalizeResolverRef.current(blob);
						finalizeResolverRef.current = null;
					}
				} else {
					if (finalizeResolverRef.current) {
						finalizeResolverRef.current(null);
						finalizeResolverRef.current = null;
					}
				}
				setIsRecording(false);
				setIsPaused(false);
			};

			mediaRecorderRef.current = mediaRecorder;
			mediaRecorder.start(500);
			setIsRecording(true);
			setIsPaused(false);
			setHasRecording(false);
			setDurationMs(0);
			durationMsRef.current = 0;
			recordedBlobRef.current = null;

			clearRecordingTimer();
			const startedAt = Date.now();
			recordingTimerRef.current = window.setInterval(() => {
				const elapsed = Date.now() - startedAt;
				durationMsRef.current = elapsed;
				setDurationMs(elapsed);
				if (elapsed >= MAX_RECORDING_DURATION_MS) {
					if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
						mediaRecorderRef.current.stop();
					}
					clearRecordingTimer();
				}
			}, 250);
		} catch (err) {
			message.error('Unable to start audio recording. Please check microphone permissions.');
		}
	}, [clearRecordingTimer, stopMediaStream]);

	const togglePause = useCallback(() => {
		if (!mediaRecorderRef.current) {
			return;
		}
		if (mediaRecorderRef.current.state === 'recording') {
			try {
				mediaRecorderRef.current.pause();
			} catch (e) {
				return;
			}
			setIsPaused(true);
			clearRecordingTimer();
		} else if (mediaRecorderRef.current.state === 'paused') {
			stopPreview();
			try {
				mediaRecorderRef.current.resume();
			} catch (e) {
				return;
			}
			setIsPaused(false);
			clearRecordingTimer();
			const resumeStartedAt = Date.now() - durationMsRef.current;
			recordingTimerRef.current = window.setInterval(() => {
				const elapsed = Date.now() - resumeStartedAt;
				durationMsRef.current = elapsed;
				setDurationMs(elapsed);
				if (elapsed >= MAX_RECORDING_DURATION_MS) {
					if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
						mediaRecorderRef.current.stop();
					}
					clearRecordingTimer();
				}
			}, 250);
		}
	}, [clearRecordingTimer, stopPreview]);

	const playPreview = useCallback(() => {
		if (recordingChunksRef.current.length === 0) {
			return;
		}

		stopPreview();

		const blob = new Blob(recordingChunksRef.current, { type: 'audio/webm' });
		const url = URL.createObjectURL(blob);
		previewUrlRef.current = url;

		const audio = new Audio(url);
		previewAudioRef.current = audio;

		audio.onended = () => {
			stopPreview();
		};

		const recordedDuration = durationMsRef.current;

		audio
			.play()
			.then(() => {
				setIsPlaying(true);
				previewTimerRef.current = window.setInterval(() => {
					if (previewAudioRef.current) {
						const currentTimeMs = previewAudioRef.current.currentTime * 1000;
						setPlaybackProgress(Math.min(currentTimeMs / recordedDuration, 1));
					}
				}, 50);
			})
			.catch(() => {
				stopPreview();
			});
	}, [stopPreview]);

	const deleteRecording = useCallback(() => {
		resetRecordingState();
	}, [resetRecordingState]);

	const finalizeRecording = useCallback((): Promise<Blob | null> => {
		if (recordedBlobRef.current) {
			const existingBlob = recordedBlobRef.current;
			resetRecordingState();
			return Promise.resolve(existingBlob);
		}

		if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
			return new Promise((resolve) => {
				finalizeResolverRef.current = (blob) => {
					resolve(blob);
				};
				try {
					isResettingRef.current = false; // ensure onstop processes the blob
					mediaRecorderRef.current.stop();
				} catch (e) {
					finalizeResolverRef.current = null;
					resolve(null);
				}
			});
		}

		return Promise.resolve(null);
	}, [resetRecordingState]);

	return {
		isRecording,
		isPaused,
		hasRecording,
		durationMs,
		stream,
		isPlaying,
		playbackProgress,
		startRecording,
		togglePause,
		deleteRecording,
		finalizeRecording,
		playPreview,
		stopPreview,
	};
};
